import { useState } from 'react';
import { Link } from 'react-router-dom';
import { BalanceCard } from '@/components/account';
import {
  TransactionHistory,
  PaymentForm,
  BillingExport,
} from '@/components/billing';

type TabType = 'transactions' | 'export';

export default function BillingPage() {
  const [activeTab, setActiveTab] = useState<TabType>('transactions');
  const [showPaymentForm, setShowPaymentForm] = useState(false);

  const tabs: { id: TabType; label: string }[] = [
    { id: 'transactions', label: 'Transaction History' },
    { id: 'export', label: 'Export Data' },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Billing & Transactions</h1>
          <p className="text-sm text-gray-600 mt-1">Manage your account balance and payments</p>
        </div>
        <Link
          to="/account"
          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
        >
          Back to Account
        </Link>
      </div>

      {/* Payment Form Modal */}
      {showPaymentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="max-w-lg w-full">
            <PaymentForm
              onSuccess={() => setShowPaymentForm(false)}
              onCancel={() => setShowPaymentForm(false)}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar - Balance Card */}
        <div className="lg:col-span-1">
          <BalanceCard onAddFunds={() => setShowPaymentForm(true)} showAddButton={true} />

          {/* Quick Stats */}
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
            <div className="space-y-3">
              <button
                onClick={() => setActiveTab('export')}
                className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                Export Transactions
              </button>
              <Link
                to="/account"
                className="block w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium text-center"
              >
                Account Settings
              </Link>
            </div>
          </div>
        </div>

        {/* Main Content - Tabs */}
        <div className="lg:col-span-3">
          {/* Tab Navigation */}
          <div className="bg-white rounded-t-lg shadow-md border-b border-gray-200">
            <nav className="flex space-x-1 p-2" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-colors
                    ${
                      activeTab === tab.id
                        ? 'bg-indigo-100 text-indigo-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="mt-0">
            {activeTab === 'transactions' && <TransactionHistory />}
            {activeTab === 'export' && <BillingExport />}
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="mt-8 bg-indigo-50 border border-indigo-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-indigo-900 mb-3">Billing Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-indigo-800">
          <div>
            <h3 className="font-medium mb-2">How Credits Work</h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>Purchase credit packages (20 to 200 credits)</li>
              <li>1 credit per high-confidence search</li>
              <li>0.5 credits for medium-confidence results</li>
              <li>Free for low-confidence results</li>
              <li>Credits never expire</li>
            </ul>
          </div>
          <div>
            <h3 className="font-medium mb-2">Payment Security</h3>
            <ul className="space-y-1 list-disc list-inside">
              <li>All payments processed via Stripe</li>
              <li>PCI-DSS compliant security</li>
              <li>Your card details are never stored</li>
              <li>Instant credit to your account</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
